import { LoadingModalComponent } from '../../../components/modals/variants/LoadingModalComponent';
import {
  EntityDependencies,
  TRPCMethodEnum,
  TRPCResourceEnum
} from '../../../api/trpcApi/types';
import { useLocalStorage, useTRPCRequest } from '../../../hooks';
import { ListThumbnail } from '../../../components/images';
import { SearchBar } from '../../../components/search-bar';
import { SearchableCombobox } from '../../../components/inputs';
import { TableRecord } from '../../../types/record-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { RecordTable } from '../../../components/tables';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Style } from '../types';
import {
  DuplicateAssetButton,
  DeleteAssetButton
} from '../../../components/buttons';
import { useStyles } from './hooks';
import {
  CannotDeleteModalComponent,
  ConfirmDeleteModalComponent
} from '../../../components/modals';
import { Title } from '../../../components/Title';
import { usePartners } from '../../partners/hooks';

export function StylesList() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useLocalStorage<string>(
    window.location.pathname,
    ''
  );
  const [selectedPartnerId, setSelectedPartnerId] = useLocalStorage<string>(
    `${window.location.pathname}-partnerId`,
    'base'
  );
  const { handleTRPCRequest } = useTRPCRequest();
  const [styleToDelete, setStyleToDelete] = useState<TableRecord | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
  const [showCannotDeleteModal, setShowCannotDeleteModal] = useState(false);
  const [dependencies, setDependencies] = useState<EntityDependencies>({});

  const { activePartners } = usePartners();

  const partnerOptions = useMemo(
    () =>
      activePartners.map(partner => ({
        value: partner.partner_id!,
        label: partner.name
      })),
    [activePartners]
  );

  // Validate selectedPartnerId and reset to 'base' if it's not a valid option
  useEffect(() => {
    if (activePartners.length === 0) {
      // Partners haven't loaded yet, skip validation
      return;
    }
    const isValidSelection =
      selectedPartnerId === 'base' ||
      activePartners.some(p => p.partner_id === selectedPartnerId);

    if (!isValidSelection) {
      setSelectedPartnerId('base');
    }
  }, [activePartners, selectedPartnerId, setSelectedPartnerId]);

  const { styles, isLoading, refetch } = useStyles({
    search: searchTerm,
    filterType: selectedPartnerId === 'base' ? 'base' : 'partner',
    partnerId: selectedPartnerId === 'base' ? undefined : selectedPartnerId
  });

  const handleAddNew = useCallback(() => {
    navigate(`/styles/new`);
  }, [navigate]);

  const handleDuplicateStyle = useCallback(
    (style: TableRecord) => (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      event.preventDefault();
      navigate('/styles/new', { state: { styleIdToCopy: style.style_id } });
    },
    [navigate]
  );
  const handleDeleteStyle = useCallback(
    (style: TableRecord) => (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      event.preventDefault();
      setStyleToDelete(style);
      setConfirmDelete(true);
    },
    []
  );

  const handleConfirmDeleteStyle = useCallback(async () => {
    if (styleToDelete) {
      setConfirmDelete(false);
      const { style_id } = styleToDelete as Style;
      const { res, error } = await handleTRPCRequest({
        method: TRPCMethodEnum.delete,
        resourceType: TRPCResourceEnum.styles,
        requestBody: { style_id }
      });

      if (!error && res.success) {
        toast.success('Style deleted successfully!');
        refetch();
      }

      if (!res?.success) {
        setShowCannotDeleteModal(true);
        setDependencies(res.relatedEntities);
      }
    }
  }, [styleToDelete, handleTRPCRequest, refetch]);

  const TableComponent = (
    <RecordTable
      idKey="style_id"
      data={styles ?? []}
      calculateRowHref={style => `/styles/${style.style_id}`}
      rowClassNames={[
        'card',
        'mb-[6px]',
        'flex',
        'h-[54px]',
        'cursor-pointer',
        'items-center',
        'align-middle'
      ]}
      columns={[
        {
          columnKey: 'thumbnail_path',
          onRender: (style: TableRecord) => (
            <ListThumbnail src={style.thumbnail_path} />
          ),
          widthClassNames: ['w-[54px]']
        },
        {
          columnKey: 'system_name',
          header: 'System Name',
          onRender: (style: TableRecord) => style.system_name,
          widthClassNames: ['w-[25%]', 'ml-[30px]']
        },
        {
          columnKey: 'display_name',
          header: 'Display Name',
          onRender: (style: TableRecord) => style.display_name,
          widthClassNames: ['w-[25%]']
        },
        {
          columnKey: 'partner_name',
          header: 'Partner',
          onRender: (style: TableRecord) => (
            <span
              className={
                style.partner_name ? 'text-gray-900' : 'italic text-gray-400'
              }
            >
              {style.partner_name || 'Base'}
            </span>
          ),
          widthClassNames: ['w-[15%]']
        },
        {
          columnKey: 'actions',
          widthClassNames: ['ml-auto'],
          onRender: (style: TableRecord) => {
            return (
              <div className="flex gap-3 pr-3">
                <DuplicateAssetButton
                  onDuplicateAsset={handleDuplicateStyle(style)}
                />
                <DeleteAssetButton onDeleteAsset={handleDeleteStyle(style)} />
              </div>
            );
          }
        }
      ]}
    />
  );

  if (isLoading) {
    return <LoadingModalComponent isOpen={isLoading} />;
  }

  return (
    <>
      <Title title="Styles Search" />
      <div className="mb-4 flex items-end gap-4">
        <SearchableCombobox
          label="Partner"
          value={selectedPartnerId}
          onChange={setSelectedPartnerId}
          placeholder="Search partners..."
          defaultOption={{ value: 'base', label: 'Base' }}
          options={partnerOptions}
          className="min-w-[200px]"
        />
        <SearchBar
          searchTerm={searchTerm}
          onSearch={search => setSearchTerm(search)}
          onAddNew={handleAddNew}
          classNames={['flex-1']}
        />
      </div>

      {styles.length === 0 && searchTerm === '' ? (
        <>
          <h2>No styles exist yet.</h2>
          <p> Click "+ Add New" to create your first style!</p>
        </>
      ) : null}

      {styles.length === 0 && searchTerm !== '' ? (
        <>
          <h2>No styles found for "{searchTerm}".</h2>
        </>
      ) : null}

      {styles.length > 0 && TableComponent}
      <ConfirmDeleteModalComponent
        entityName={styleToDelete?.system_name}
        isOpen={confirmDelete}
        onConfirm={handleConfirmDeleteStyle}
        onCancel={() => {
          setStyleToDelete(null);
          setConfirmDelete(false);
        }}
      />
      <CannotDeleteModalComponent
        entityName={styleToDelete?.system_name ?? ''}
        dependencies={dependencies}
        isOpen={showCannotDeleteModal}
        onCancel={() => setShowCannotDeleteModal(false)}
      />
    </>
  );
}
